import React, { useMemo } from "react";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { Dialog } from "@/components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { DeleteDialogSection } from "./DeleteDialogSection";
import { TableSection } from "./TableSection";
import { TrashDropTarget } from "./TrashDropTarget";
import { TransferSection } from "./TransferSection";
import { TransferDialogBody } from "./TransferDialogBody";
import { DropTarget } from "./DropTarget";
import { ModelElementType } from "components/entities";
import {
  TableDeletionAction,
  isDeletionAction,
} from "./DeleteModelDialogTypes";
import { TableUsageStatus, TableDeletionInfo } from "target_wasm";
import { useDeleteModel } from "./useDeleteModel";
import { AssetIcon } from "components/common/AssetIcon";
import { TransferTable, TableSectionConfig } from "./TransferTypes";
import type { DatasetUsage } from "@/lib/api/datasets";

interface DeleteModelDialogProps {
  open: boolean;
  onClose: () => void;
  model: ModelElementType | null;
  onDelete: (actions: Record<string, TableDeletionAction>) => Promise<void>;
  availableModels?: ModelElementType[];
  isFromTrash?: boolean;
  /// From the Trash: one model's row, or every model of a deleted space with
  /// `model` null.
  trashModels?: import("target_wasm").TrashModel[];
  /// A deleted space's review: the model (and sub-space) each table comes from.
  tableOrigins?: Map<string, React.ReactNode>;
  /// A model's review: the deleted space it sits in.
  location?: React.ReactNode;
}

const toTransferTable = (
  t: TableDeletionInfo,
  origins?: Map<string, React.ReactNode>,
): TransferTable => ({
  id: t.table_id_js,
  name: t.table_name_js,
  subtitle: origins?.get(t.table_id_js),
  externalLinkCount: t.external_link_count_js,
  datasetUsage: t.dataset_usage_js as DatasetUsage | undefined,
});

const SECTION_CONFIGS: TableSectionConfig[] = [
  {
    key: "critical",
    title: "Critical",
    severity: {
      color: "text-error",
      tooltip: "Critical Usage: Tables imported by other users in their Models",
    },
  },
  {
    key: "moderate",
    title: "Moderate",
    severity: {
      color: "text-warning",
      tooltip: "Moderate Usage: Tables imported in your Models",
    },
  },
  {
    key: "low",
    title: "Low",
    severity: {
      color: "text-fg-muted",
      tooltip: "Low Usage: Tables with no external imports",
    },
  },
];

const STATUS_MAP: Record<string, TableUsageStatus> = {
  critical: TableUsageStatus.Critical,
  moderate: TableUsageStatus.Moderate,
  low: TableUsageStatus.Low,
};

/// A low-risk table's section has nothing to warn about.
const importRisk = (t: TableDeletionInfo) =>
  SECTION_CONFIGS.find(
    (c) => c.key !== "low" && STATUS_MAP[c.key] === t.usage_status_js,
  )?.severity;

export const DeleteModelDialog: React.FC<DeleteModelDialogProps> = ({
  open,
  onClose,
  model,
  onDelete,
  availableModels,
  isFromTrash = false,
  trashModels,
  tableOrigins,
  location,
}) => {
  const {
    loading,
    error,
    deleting,
    analysis,
    expandedSections,
    modelSearchQuery,
    stagedActions,
    totalTables,
    canDelete,
    selectedTables,
    activeId,
    batchIds,
    successIds,
    fadingIds,
    failedIds,
    availableModels: filteredModels,
    executeTransfer,
    executeBatchTransfer,
    executeDelete,
    handleCancel,
    toggleSection,
    getUnassignedTables,
    getAssignedTablesForModel,
    selectAllTables,
    handleTableClick,
    setModelSearchQuery,
    dndHandlers,
    dragState,
    transferSectionRef,
  } = useDeleteModel(
    open,
    model,
    onDelete,
    onClose,
    availableModels,
    trashModels,
  );

  const activeDragTables = useMemo(() => {
    if (!dragState.activeId || !analysis?.tables_js) return [];
    const tables = analysis.tables_js;
    if (selectedTables.has(dragState.activeId)) {
      return tables
        .filter((t) => selectedTables.has(t.table_id_js))
        .map((t) => toTransferTable(t, tableOrigins));
    }
    return tables
      .filter((t) => t.table_id_js === dragState.activeId)
      .map((t) => toTransferTable(t, tableOrigins));
  }, [dragState.activeId, analysis, selectedTables, tableOrigins]);

  const draggedTableIds = useMemo(() => {
    if (!dragState.activeId) return new Set<string>();
    return selectedTables.has(dragState.activeId)
      ? selectedTables
      : new Set([dragState.activeId]);
  }, [dragState.activeId, selectedTables]);

  const sections = useMemo(() => {
    if (!analysis?.tables_js) return [];
    return SECTION_CONFIGS.map((config) => {
      const status = STATUS_MAP[config.key];
      const allOfStatus = analysis.tables_js!.filter(
        (t) => t.usage_status_js === status,
      );
      const unassigned = getUnassignedTables(allOfStatus);
      return {
        config,
        tables: unassigned.map((t) => toTransferTable(t, tableOrigins)),
        raw: unassigned,
      };
    }).filter((s) => s.tables.length > 0);
  }, [analysis, getUnassignedTables, tableOrigins]);

  const rawTrashTables = useMemo(() => {
    if (!analysis?.tables_js) return [];
    return analysis.tables_js.filter((t) =>
      isDeletionAction(stagedActions[t.table_id_js]),
    );
  }, [analysis, stagedActions]);

  const trashTables = useMemo(
    () =>
      rawTrashTables.map((t) => ({
        ...toTransferTable(t, tableOrigins),
        risk: importRisk(t),
      })),
    [rawTrashTables, tableOrigins],
  );

  const transferPanel = (
    <TransferSection
      isActive={dragState.isOverTransferSection}
      filterQuery={modelSearchQuery}
      setFilterQuery={setModelSearchQuery}
      transferSectionRef={transferSectionRef}
      filterPlaceholder="Search models"
      filterType="search"
      emptyMessage={
        modelSearchQuery.trim()
          ? "No models match your search."
          : "No models available for transfer"
      }
    >
      {filteredModels.length > 0
        ? filteredModels.map((modelItem) => (
            <DropTarget
              key={modelItem.model_id}
              targetId={modelItem.model_id}
              droppablePrefix="model"
              isDragOver={dragState.overModelId === modelItem.model_id}
              assignedTables={getAssignedTablesForModel(modelItem.model_id).map(
                (t) => toTransferTable(t, tableOrigins),
              )}
              onConfirmTransfer={async (tableId, targetModelId) => {
                await executeTransfer(tableId, targetModelId);
              }}
              onTransferAll={async (targetModelId) => {
                const rawTables = getAssignedTablesForModel(targetModelId);
                await executeBatchTransfer(targetModelId, rawTables);
              }}
              transferringTableId={activeId}
              transferringBatchIds={batchIds}
              transferSuccessTableIds={successIds}
              fadingOutTableIds={fadingIds}
              transferErrorTableIds={failedIds}
              selectedTables={selectedTables}
              handleTableClick={handleTableClick}
              draggedTableIds={draggedTableIds}
              renderHeader={() => (
                <>
                  <AssetIcon
                    size="sm"
                    color={modelItem.color}
                    iconIndex={modelItem.icon}
                    className="w-3.5! h-3.5!"
                  />
                  <span className="text-fg-medium flex-1 cursor-default select-none">
                    {modelItem.name}
                  </span>
                </>
              )}
            />
          ))
        : null}
    </TransferSection>
  );

  const tablesPanel = (
    <DeleteDialogSection
      icon={
        <TableChartOutlinedIcon className="icon-sm text-fg-muted-regular" />
      }
      title="Tables"
      className="h-full min-h-0 p-4"
    >
      <div className="space-y-2">
        {sections.map(({ config, tables, raw }) => (
          <TableSection
            key={config.key}
            title={config.title}
            count={tables.length}
            tables={tables}
            expanded={
              expandedSections[config.key as keyof typeof expandedSections]
            }
            onToggleExpanded={() =>
              toggleSection(config.key as keyof typeof expandedSections)
            }
            selectedTables={selectedTables}
            onToggleSelection={(tableId, e) =>
              handleTableClick(
                tableId,
                e,
                raw.map((t) => t.table_id_js),
              )
            }
            onSelectAll={() => selectAllTables(raw)}
            draggedTableIds={draggedTableIds}
            transferringTableId={activeId}
            transferringBatchIds={batchIds}
            transferSuccessTableIds={successIds}
            fadingOutTableIds={fadingIds}
            severity={config.severity}
          />
        ))}
      </div>
    </DeleteDialogSection>
  );

  return (
    <Dialog
      open={open}
      title={model ? "Deleting Model" : "Deleting Space"}
      onClose={handleCancel}
      className="w-175"
      actions={
        isFromTrash ? undefined : (
          <div className="flex justify-end space-x-2">
            <Button
              variant="destructive"
              onClick={executeDelete}
              disabled={
                !canDelete || deleting || activeId !== null || batchIds.size > 0
              }
              loading={deleting}
            >
              Schedule for deletion
            </Button>
          </div>
        )
      }
    >
      <TransferDialogBody
        dndHandlers={dndHandlers}
        loading={loading}
        error={error}
        description={
          totalTables > 0 ? (
            <div className="text-fg-medium">
              This {model ? "model" : "space"}
              {location && <> in {location}</>} contains{" "}
              <strong>{totalTables}</strong> table
              {totalTables !== 1 ? "s" : ""}{" "}
              {isFromTrash
                ? "that are scheduled for deletion"
                : "that need to be handled"}
              .
            </div>
          ) : undefined
        }
        layout={isFromTrash ? "single-transfer" : "split"}
        tablesPanel={tablesPanel}
        transferPanel={transferPanel}
        trashPanel={
          <TrashDropTarget
            tables={trashTables}
            isDragActive={dragState.activeId !== null}
            selectedTables={selectedTables}
            onToggleSelection={(tableId, e) => {
              handleTableClick(
                tableId,
                e,
                trashTables.map((t) => t.id),
              );
            }}
            onSelectAll={() => selectAllTables(rawTrashTables)}
            draggedTableIds={draggedTableIds}
          />
        }
        activeDragTables={activeDragTables}
        dragActiveId={dragState.activeId}
        isOverTrash={dragState.isOverTrash}
      />
    </Dialog>
  );
};
